import React, { useState, useEffect } from 'react';
import { teamAPI, locationsAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import '../Shared/FormStyles.scss';
import './TeamManager.scss';

const TeamManager = () => {
  const [members, setMembers] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    bio: '',
    image: '',
    email: '',
    phone: '',
    locations: [],
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchMembers();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAll();
      setAllLocations(response.data);
    } catch (err) {
      console.error('Failed to fetch locations', err);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await teamAPI.getAllAdmin();
      setMembers(response.data);
    } catch (err) {
      setError('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await teamAPI.update(editing._id, formData);
        setSuccess('Team member updated successfully');
      } else {
        await teamAPI.create(formData);
        setSuccess('Team member created successfully');
      }
      resetForm();
      await fetchMembers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save team member');
    }
  };

  const handleEdit = (member) => {
    setEditing(member);
    setFormData({
      name: member.name || '',
      role: member.role || '',
      bio: member.bio || '',
      image: member.image || '',
      email: member.email || '',
      phone: member.phone || '',
      locations: (member.locations || []).map((loc) => (typeof loc === 'object' ? loc._id : loc)),
      isActive: member.isActive !== undefined ? member.isActive : true,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;

    try {
      await teamAPI.delete(id);
      setSuccess('Team member deleted successfully');
      fetchMembers();
    } catch (err) {
      setError('Failed to delete team member');
    }
  };

  const handleLocationToggle = (locationId) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.includes(locationId)
        ? prev.locations.filter((id) => id !== locationId)
        : [...prev.locations, locationId],
    }));
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      name: '',
      role: '',
      bio: '',
      image: '',
      email: '',
      phone: '',
      locations: [],
      isActive: true,
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="team-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Team Member' : 'Add New Team Member'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, name: val })); }}
              required
            />
          </div>
          <div className="form-group">
            <label>Role</label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, role: val })); }}
            />
          </div>
          <ImageUploader
            value={formData.image}
            onChange={(url) => setFormData((prev) => ({ ...prev, image: url }))}
            label="Member Photo"
          />
          <div className="form-group">
            <label>Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, bio: val })); }}
              rows={4}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, email: val })); }}
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, phone: val })); }}
            />
          </div>
          {allLocations.length > 0 && (
            <div className="form-group">
              <label>Locations</label>
              <div className="locations-checklist">
                {allLocations.map((loc) => (
                  <label key={loc._id} className="checklist-item">
                    <input
                      type="checkbox"
                      checked={formData.locations.includes(loc._id)}
                      onChange={() => handleLocationToggle(loc._id)}
                    />
                    {loc.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => { const val = e.target.checked; setFormData((prev) => ({ ...prev, isActive: val })); }}
              />
              Active
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Team Members</h2>
        {members.length === 0 ? (
          <div className="empty-state">No team members found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Image</th>
                <th>Bio</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Locations</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member._id}>
                  <td>{member.name}</td>
                  <td>{member.role}</td>
                  <td>
                    {member.image && (
                      <img src={member.image} alt={member.name} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%' }} />
                    )}
                  </td>
                  <td className="bio-cell">{member.bio || '—'}</td>
                  <td>{member.email || '—'}</td>
                  <td>{member.phone || '—'}</td>
                  <td>
                    {(member.locations || [])
                      .map((loc) => (typeof loc === 'object' ? loc.name : loc))
                      .join(', ') || '—'}
                  </td>
                  <td>{member.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(member)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(member._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TeamManager;
